import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
    CoverageKpis,
    FunnelKpis,
    FunnelTypeStat,
    NearCertainItem,
    getCoverageKpis,
    getFunnelKpis,
    getNearCertainPreview,
} from "../../../interfaces/enrichmentKpis";

// Funnel, coverage and near-certain sample for /bo/enrichment-kpis.
//
// Funnel segments use the validated categorical order (blue, orange, aqua, yellow — dataviz
// validator, light surface): approved, to review, not searched yet, no result; rejected + closed
// in neutral grey. Aqua and yellow sit below 3:1 on white, so every value is also printed in the
// table next to the bar.

const fmt = (n: number) => (n ?? 0).toLocaleString("fr-FR");
const pct = (n: number, total: number) => (total > 0 ? `${((100 * n) / total).toFixed(1).replace(".", ",")} %` : "—");

export const FUNNEL_TYPE_LABELS: Record<string, string> = {
    "event:google_place_DFS": "Google Maps (DFS)",
    "event:google_place": "Google Maps (ancien)",
    "event:website": "Site web",
    "event:website_content_OTS": "Email entreprise (Outscraper)",
    "event:website_crawl": "Lecture du site (crawl maison)",
    "event:osm_place": "OpenStreetMap",
    "event:datatourisme_place": "DATAtourisme",
    "event:company_email": "Email entreprise",
    "event:owner_email": "Email dirigeant (trouvé sur le site)",
    "event:siren": "SIREN",
    "event:address": "Adresse",
    "event:restaurant_name": "Enseigne",
    "event:franchise": "Franchise",
    "event:company_linked": "Entreprise liée",
    "event:instagram": "Instagram",
    "event:facebook": "Facebook",
    "event:tiktok": "TikTok",
    "event:linkedin": "LinkedIn entreprise",
    "person:linkedin": "LinkedIn dirigeant",
    "person:email": "Email perso dirigeant",
    "person:birthdate": "Date de naissance",
};

const SEGMENTS: Array<{ key: keyof FunnelTypeStat | "other"; label: string; color: string }> = [
    { key: "approved", label: "Approuvés", color: "#2a78d6" },
    { key: "toReview", label: "Trouvés, à relire", color: "#eb6834" },
    { key: "pending", label: "Pas encore cherchés", color: "#1baf7a" },
    { key: "noResult", label: "Sans résultat", color: "#eda100" },
    { key: "other", label: "Rejetés / fermés", color: "#c4c2bc" },
];

const segValue = (t: FunnelTypeStat, key: string) =>
    key === "other" ? t.rejected + t.closed : key === "pending" ? t.pending + t.inProgress : ((t as any)[key] as number);

function Computed({ at, stale, onRefresh, busy }: { at?: string; stale?: boolean; onRefresh: () => void; busy: boolean }) {
    return (
        <div className="flex items-center gap-3 text-xs text-gray-400">
            {at && (
                <span>
                    Calculé à {new Date(at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    {stale && " · recalcul en cours"}
                </span>
            )}
            <button onClick={onRefresh} disabled={busy} className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                Recalculer
            </button>
        </div>
    );
}

// ─── Funnel ───────────────────────────────────────────────────────────────────
export function FunnelTab() {
    const [data, setData] = useState<FunnelKpis | null>(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = (refresh = false) => {
        setBusy(true);
        setError(null);
        getFunnelKpis(refresh)
            .then(setData)
            .catch(() => setError("Impossible de charger l'entonnoir (le premier calcul peut prendre quelques minutes)."))
            .finally(() => setBusy(false));
    };
    useEffect(() => load(), []);

    return (
        <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-gray-900">Où en est chaque enrichissement</p>
                    <p className="text-xs text-gray-400">
                        Toutes les entrées d'enrichissement, par état — montre si les données manquent faute d'être cherchées, trouvées mais pas relues, ou introuvables.
                    </p>
                </div>
                <Computed at={data?.computedAt} stale={data?.stale} onRefresh={() => load(true)} busy={busy} />
            </div>
            <div className="px-4 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 border-b border-gray-100">
                {SEGMENTS.map((s) => (
                    <span key={s.key} className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                        {s.label}
                    </span>
                ))}
            </div>
            {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}
            {!data && busy && <p className="px-4 py-3 text-sm text-gray-500">Calcul en cours… (parcourt tous les événements)</p>}
            {data && (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-gray-500">
                                <th className="text-left font-medium px-4 py-2">Enrichissement</th>
                                <th className="text-left font-medium px-2 py-2 w-[28%]">Répartition</th>
                                {SEGMENTS.map((s) => (
                                    <th key={s.key} className="text-right font-medium px-2 py-2 whitespace-nowrap">{s.label}</th>
                                ))}
                                <th className="text-right font-medium px-4 py-2">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.types.map((t) => (
                                <tr key={t.key} className="border-t border-gray-100">
                                    <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-800">
                                        {FUNNEL_TYPE_LABELS[t.key] ?? t.type}
                                        {t.entity === "person" && <span className="ml-1 text-gray-400 font-normal">(personne)</span>}
                                    </td>
                                    <td className="px-2 py-2">
                                        <div className="flex h-3 w-full rounded-sm overflow-hidden bg-gray-100 gap-[2px]">
                                            {SEGMENTS.map((s) => {
                                                const v = segValue(t, s.key);
                                                if (!v) return null;
                                                return (
                                                    <div
                                                        key={s.key}
                                                        title={`${s.label} : ${fmt(v)} (${pct(v, t.total)})`}
                                                        style={{ width: `${(100 * v) / t.total}%`, background: s.color }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </td>
                                    {SEGMENTS.map((s) => {
                                        const v = segValue(t, s.key);
                                        const levels = s.key === "toReview" ? Object.entries(t.toReviewByLevel || {}) : [];
                                        return (
                                            <td
                                                key={s.key}
                                                className={`text-right px-2 py-2 tabular-nums ${v ? "text-gray-700" : "text-gray-300"}`}
                                                title={levels.length ? levels.map(([l, n]) => `${l} : ${fmt(n)}`).join(" · ") : undefined}
                                            >
                                                {fmt(v)}
                                            </td>
                                        );
                                    })}
                                    <td className="text-right px-4 py-2 tabular-nums font-semibold text-gray-900">{fmt(t.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── Coverage ─────────────────────────────────────────────────────────────────
const COVERAGE_FIELDS: Array<{ key: string; label: string }> = [
    { key: "phone", label: "Téléphone" },
    { key: "website", label: "Site web" },
    { key: "email", label: "Email" },
    { key: "anySocial", label: "Au moins un réseau social" },
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "openingHours", label: "Horaires" },
    { key: "googleRating", label: "Note Google" },
    { key: "owners", label: "Dirigeant(s)" },
    { key: "enseigne", label: "Enseigne" },
    { key: "description", label: "Description" },
    { key: "priceRange", label: "Gamme de prix" },
    { key: "terraceKnown", label: "Terrasse (oui / non connu)" },
    { key: "geo", label: "Coordonnées GPS" },
];

const SOURCE_LABELS: Record<string, string> = {
    google: "Google",
    osm: "OpenStreetMap",
    datatourisme: "DATAtourisme",
    website_crawl: "Lecture du site",
    outscraper: "Outscraper",
    website_finder: "Recherche de site",
    website_contacts: "Réseaux du site",
    legacy: "Valeur antérieure",
};

export function CoverageTab() {
    const [data, setData] = useState<CoverageKpis | null>(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = (refresh = false) => {
        setBusy(true);
        setError(null);
        getCoverageKpis(refresh)
            .then(setData)
            .catch(() => setError("Impossible de charger la couverture."))
            .finally(() => setBusy(false));
    };
    useEffect(() => load(), []);

    const total = data?.counts.total ?? 0;
    const sources = new Map<string, Array<{ source: string; count: number }>>();
    for (const s of data?.bySource ?? []) (sources.get(s.field) || sources.set(s.field, []).get(s.field)!).push(s);
    const fieldOfKey: Record<string, string> = { phone: "phone", website: "website_url", email: "email", openingHours: "operations.opening_hours", instagram: "social:instagram", facebook: "social:facebook", description: "description", priceRange: "price_range" };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-sm text-gray-600">
                    Part des établissements <b>actifs et visibles</b> qui ont chaque donnée, quelle que soit la source.
                    {data && <span className="text-gray-400"> Base : {fmt(total)} établissements · sans aucun contact : {fmt(data.counts.nothing)} ({pct(data.counts.nothing, total)})</span>}
                </p>
                <Computed at={data?.computedAt} stale={data?.stale} onRefresh={() => load(true)} busy={busy} />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {!data && busy && <p className="text-sm text-gray-500">Calcul en cours…</p>}
            {data && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {COVERAGE_FIELDS.map((f) => {
                        const n = data.counts[f.key] ?? 0;
                        const bySource = (sources.get(fieldOfKey[f.key]) || []).sort((a, b) => b.count - a.count);
                        return (
                            <div key={f.key} className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-2">
                                <span className="text-xs font-medium text-gray-500">{f.label}</span>
                                <div>
                                    <p className="text-xl font-semibold leading-tight text-gray-900">{pct(n, total)}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">{fmt(n)} établissements</p>
                                </div>
                                <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${total ? (100 * n) / total : 0}%`, background: "#2a78d6" }} />
                                </div>
                                {bySource.length > 0 && (
                                    <div className="border-t border-gray-100 pt-2 flex flex-col gap-0.5 text-xs" title="Valeurs gardées par source (principale ou secondaire)">
                                        {bySource.slice(0, 4).map((s) => (
                                            <div key={s.source} className="flex justify-between text-gray-500">
                                                <span>{SOURCE_LABELS[s.source] ?? s.source}</span>
                                                <span className="tabular-nums">{fmt(s.count)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Near-certain sample ──────────────────────────────────────────────────────
const KIND_LABELS: Record<string, string> = {
    website_crawl: "Lecture du site",
    social: "Réseau social (Outscraper)",
    restaurant_name: "Enseigne",
};

function describe(item: NearCertainItem): string {
    const v = item.value || {};
    if (item.kind === "website_crawl") {
        const parts = [v.email && `email ${v.email}`, v.phone && `tél. ${v.phone}`, ...Object.entries(v.socials || {}).map(([p, u]) => `${p} ${u}`)];
        return parts.filter(Boolean).join(" · ");
    }
    if (item.kind === "social") return `${item.type} ${v.url}`;
    return v.restaurant_name ?? "";
}

export function NearCertainTab() {
    const [kind, setKind] = useState<string>("");
    const [data, setData] = useState<{ enabled: boolean; items: NearCertainItem[] } | null>(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setBusy(true);
        setError(null);
        getNearCertainPreview(kind || undefined, 100)
            .then(setData)
            .catch(() => setError("Impossible de charger l'échantillon."))
            .finally(() => setBusy(false));
    }, [kind]);

    return (
        <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-gray-900">Échantillon de l'auto-approbation « quasi-certaine »</p>
                    <p className="text-xs text-gray-400">
                        Exactement ce que le cron approuverait, sans rien écrire. Vérifiez-le avant d'activer <code>autoApprove:near_certain</code> dans /bo/services
                        {data && (data.enabled ? " — actuellement ACTIVÉ." : " — actuellement désactivé.")}
                    </p>
                </div>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">
                    <option value="">Tous les types</option>
                    {Object.entries(KIND_LABELS).map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                    ))}
                </select>
            </div>
            {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}
            {busy && <p className="px-4 py-3 text-sm text-gray-500">Chargement…</p>}
            {data && !busy && data.items.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">Rien à approuver pour l'instant.</p>}
            {data && !busy && data.items.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-gray-500">
                                <th className="text-left font-medium px-4 py-2">Établissement</th>
                                <th className="text-left font-medium px-2 py-2">Type</th>
                                <th className="text-left font-medium px-2 py-2">Valeur écrite</th>
                                <th className="text-left font-medium px-4 py-2">Pourquoi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((it, i) => (
                                <tr key={`${it.eventId}-${it.type}-${i}`} className="border-t border-gray-100 align-top">
                                    <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">{it.companyName}</td>
                                    <td className="px-2 py-2 text-gray-600 whitespace-nowrap">{KIND_LABELS[it.kind] ?? it.kind}</td>
                                    <td className="px-2 py-2 text-gray-700 break-all">{describe(it)}</td>
                                    <td className="px-4 py-2 text-gray-500">{it.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
